<template>
    <div class="compare-patent-list pa-text-gray-1100 pa-h-full pa-w-full pa-flex pa-flex-col">
        <div v-if="initLoading" class="pa-h-full pa-flex pa-items-center pa-justify-center">
            <PtIcon
                class="!pa-text-[32px]"
                icon="SolidLoadingBars"
            />
        </div>
        <template v-else>
            <div
                class="compare-patent-list__header pa-py-2 pa-px-4 pa-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.14),0px_2px_6px_0_rgba(0,0,0,0.14)] pa-shrink-0"
            >
                <p class="pa-leading-5">
                    <span class="pa-font-semibold">{{ totalNumber?.toLocaleString() }}</span>
                    <span class="pa-text-gray-450">{{ t('patent-view.Results') }}</span>
                </p>
                <p class="pa-mt-1 pa-text-gray-90">
                    <span>{{ displayedSubTitle }}</span>
                </p>
            </div>
            <div
                ref="list"
                class="compare-patent-list__main pa-flex-1 pa-overflow-auto"
            >
                <div
                    v-if="hasPrevPage"
                    class="pa-flex pa-items-center pa-justify-center pa-relative pa-h-[32px]"
                >
                    <PtIcon
                        v-show="prevPageLoading"
                        icon="SolidLoadingBars"
                    />
                    <span
                        v-show="!prevPageLoading"
                        class="pa-text-blue-500 pa-cursor-pointer"
                        @click="loadPrevPage"
                    >
                        &lt;&lt; {{ t('patent-view["Prev page"]') }}
                    </span>
                </div>
                <div
                    v-for="(patent, index) in patentList"
                    :key="`${index}__${patent.PATENT_ID}`"
                    :data-patentId="patent.PATENT_ID"
                    class="pa-block pa-px-4 pa-py-3 pa-cursor-pointer hover:pa-bg-gray-20"
                    :class="{
                        'pa-bg-gray-30': rightId === patent.PATENT_ID,
                    }"
                    @click.stop="changePatent(patent)"
                >
                    <div class="pa-text-xs pa-flex pa-items-center">
                        <span class="pa-text-gray-450 pa-mr-3">{{ startIndex + index + 1 }}</span>
                        <span class="pa-text-green-600">{{ patent.PN }}</span>
                    </div>
                    <p
                        class="pa-mt-1 pa-h-[40px] pa-leading-5 pa-line-clamp-2 pa-text-ellipsis"
                        :title="patent.TITLE"
                        v-html="patent.TITLE"
                    />
                </div>
                <!-- next -->
                <div
                    v-if="hasNextPage"
                    class="pa-flex pa-items-center pa-justify-center pa-relative pa-h-[32px]"
                >
                    <PtIcon
                        v-show="nextPageLoading"
                        icon="SolidLoadingBars"
                    />
                    <span
                        v-show="!nextPageLoading"
                        class="pa-text-blue-500 pa-cursor-pointer"
                        @click="loadPrevPage"
                    >
                        {{ t('patent-view["Next page"]') }} >>
                    </span>
                    <PtLazy
                        v-if="!nextPageLoading"
                        :key="`lazy-${nextPage}`"
                        :options="{ delay: 200 }"
                        :lazyTask="loadNextPage"
                        class="pa-h-full pa-w-full pa-absolute pa-top-0 pa-left-0"
                    />
                </div>
            </div>
            <div class="compare-patent-list__footer pa-p-4 pa-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.14),0px_3px_8px_0_rgba(0,0,0,0.24)] pa-shrink-0">
                <AddPatent
                    :placeholder="t('patent-view.compareView.addPatent')"
                    @submit="addPatentToList"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, computed } from 'vue';
import { useI18n } from '@/composables/use-i18n';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { Lazy as PtLazy, Icon as PtIcon } from 'patsnap-biz';
import _ from 'lodash';
import AddPatent from '@/components/add-patent/AddPatent.vue';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { GetCompareList, AddComparePatent, type ApiGetCompareListResponse } from '@/infrastructure/api-functions/core-search-api/core-search-api-service.interface';
import { useRoute, useRouter } from 'vue-router/composables';
import { useCompareInit } from '@/composables/use-compare-init';
import { create } from '@/utils/deferred';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg'

interface PatentItem {
    PATENT_ID: string;
    PN: string;
    TITLE: string;
    page?: string;
}

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { compareId, page, rows, sort, _type, isTargetEmpty, rightId } = useCompareUrlState();
const { deferred } = useCompareInit();

const displayedSubTitle = computed(() => {
    const supportDisplayedTypes = ['family', 'cite', 'cited', 'mlt', 'mlt_image', 'semantic', 'condensed_family'];
    if (_type.value && supportDisplayedTypes.includes(_type.value)) {
        return t(`patent-view.list-type.${_type.value}`);
    }
    return t('patent-view.compareView.selectedPatents');
})

const initLoading = ref(false);
const patentList = ref<PatentItem[]>([])
const totalNumber = ref(0);
const startIndex = ref(0);

const prePage = ref(page.value);
const nextPage = ref(page.value);

const hasNextPage = ref(false);
const hasPrevPage = ref(false);

const getCompareList = (page:number, callback:(res:ApiGetCompareListResponse) => void) => {
    const service = injectService<GetCompareList>(GetCompareList);
    service.getCompareList({
        compareId: compareId.value,
        page,
        rows: rows.value,
        sort: sort.value,
    }).then((res:ApiGetCompareListResponse) => {
        const _res = {
            ...res,
            LIST: res?.LIST?.map((item) => {
                return {
                    ...item,
                    page,
                }
            })
        }
        callback && callback(_res);
    }).catch(err => {
        console.log(err)
    });
}

const initLoadList = () => {
    initLoading.value = true;
    getCompareList(page.value, (res:ApiGetCompareListResponse) => {
        if (isTargetEmpty.value) {
            const defaultId = _.get(res, 'LIST[0].PATENT_ID');
            if (defaultId) {
                router.push({
                    query: route.query,
                    params: {
                        ...route.params,
                        rightId: defaultId
                    }
                })
            }
        }
        totalNumber.value = res.totalNumber || 0;
        patentList.value = res.LIST;

        hasPrevPage.value = page.value > 1;
        hasNextPage.value = page.value < res.totalPages;

        startIndex.value = (page.value - 1) * rows.value;
        initLoading.value = false;
    });
}

const nextPageLoading = ref(false);
const loadNextPage = () => {
    nextPageLoading.value = true;
    nextPage.value += 1;
    getCompareList(nextPage.value, (res:ApiGetCompareListResponse) => {
        patentList.value = Object.preventExtensions(_.concat(patentList.value, res.LIST));
        hasNextPage.value = nextPage.value < res.totalPages;
        nextPageLoading.value = false;
    });
}

const prevPageLoading = ref(false);
const loadPrevPage = () => {
    prevPageLoading.value = true;
    prePage.value -= 1;
    getCompareList(prePage.value, (res:ApiGetCompareListResponse) => {
        patentList.value = Object.preventExtensions(_.concat(res.LIST, patentList.value));
        hasPrevPage.value = prePage.value > 1;
        startIndex.value = (prePage.value - 1) * rows.value;
        prevPageLoading.value = false;
    });
}

const list = ref<HTMLElement>();
const addPatentToList = async (patent) => {
    if (!deferred.value || deferred.value?.completed) deferred.value = create();
    const patentId = patent.PATENT_ID;
    router.push({
        query: route.query,
        params: {
            ...route.params,
            rightId: patentId
        }
    })
    patentList.value.unshift(patent);
    totalNumber.value += 1;
    await nextTick();
    const service = injectService<AddComparePatent>(AddComparePatent);
    service.addComparePatent({
        compare_id: compareId.value,
        patent_id: patentId
    });
    if (list.value) {
        list.value.scrollTop = 0;
    }
}

const getRouterLink = (patent:PatentItem) => {
    return {
        query: {
            ...route.query,
            page: patent.page,
        },
        params: {
            ...route.params,
            rightId: patent.PATENT_ID
        }
    };
}

const changePatent = (patent:PatentItem) => {
    if (!deferred.value || deferred.value?.completed) deferred.value = create();
    router.push({
        query: {
            ...route.query,
            page: patent.page,
        },
        params: {
            ...route.params,
            rightId: patent.PATENT_ID
        }
    });
}

onMounted(() => {
    initLoadList();
});

</script>

<style lang="scss">

</style>
